import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { APP_PATHS } from '@/constant/app-paths';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { cn } from '@/lib/utils';
import { DAY_STATUS_LABEL_KEY } from '@/modules/time-attendance/attendance/components/day-status-badge/day-status-badge';
import {
  useGetAttendanceDays,
  useGetTodayAttendance,
} from '@/modules/time-attendance/attendance/hooks/use-attendance';
import { isOpenRecord } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { DEFAULT_MY_TIMESHEETS_ORDER } from '@/modules/time-attendance/time-tracker/graphql/queries';
import { useGetMyTimeSheets } from '@/modules/time-attendance/time-tracker/hooks/use-my-timesheet';
import {
  formatHours,
  loggedHours,
  useScheduledHours,
} from '@/modules/time-attendance/time-tracker/hooks/use-under-time';
import { TimeSheetsPagingResult } from '@/modules/time-attendance/time-tracker/types/timesheet.types';
import {
  addDaysToDay,
  orgToday,
  startOfWeekDay,
  taskDateRangeClause,
} from '@/modules/time-attendance/utils/task-date';
import { HomeCard } from '../home-card/home-card';

/** The chart's top: 10 hours (Component Map › This week chart). */
const SCALE_HOURS = 10;
const COLUMN_HEIGHT = 110;
/** Monday to Friday, as the prototype's chart. */
const WEEKDAYS = 5;

const percent = (hours: number) => `${Math.min(100, Math.max(0, (hours / SCALE_HOURS) * 100))}%`;

type DayRecord = {
  StartTimeUtc?: string | null;
  EndTimeUtc?: string | null;
  TotalWorkTimeInSecond?: number | null;
  BreakTimeDetails?: { StartTimeUtc: string; EndTimeUtc?: string | null }[] | null;
};

/**
 * Seconds worked on a record: its stored total once closed; while it is still open, the time since
 * clock-in less every break and gap between sessions so far (an open break counts up to now).
 */
export const workedSecondsOf = (record: DayRecord | null | undefined, now = new Date()) => {
  if (!record?.StartTimeUtc) return 0;
  if (!isOpenRecord(record) && record.TotalWorkTimeInSecond != null) {
    return record.TotalWorkTimeInSecond;
  }
  const nowMs = now.getTime();
  const until = record.EndTimeUtc ? new Date(record.EndTimeUtc).getTime() : nowMs;
  const away = (record.BreakTimeDetails ?? []).reduce((sum, entry) => {
    const start = new Date(entry.StartTimeUtc).getTime();
    const end = Math.min(entry.EndTimeUtc ? new Date(entry.EndTimeUtc).getTime() : nowMs, until);
    return Number.isFinite(start) && Number.isFinite(end) ? sum + Math.max(0, end - start) : sum;
  }, 0);
  return Math.max(0, Math.floor((until - new Date(record.StartTimeUtc).getTime() - away) / 1000));
};

/**
 * This week (Component Map › This week chart; changed 2 Nov 2026 to attendance): a bar per weekday,
 * Monday to Friday, of the time worked from Attendance (today's live), with the hours above it, on a
 * 10-hour scale; the day's HR-term working hours are a dashed line. A finished day under them is red;
 * today's label is primary. The tooltip adds the day's status and the hours logged in Time Tracker.
 */
export const ThisWeekCard = ({ userId }: { userId: string }) => {
  const { t, i18n } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  const today = orgToday(now);
  const weekStart = startOfWeekDay(today);
  const days = Array.from({ length: WEEKDAYS }, (_, index) => addDaysToDay(weekStart, index));
  const weekEnd = days[WEEKDAYS - 1];

  const canTimesheet = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.VIEW_TIME_TRACKER);
  const scheduledHours = useScheduledHours();
  const { data: attendanceDays, isLoading } = useGetAttendanceDays(userId, weekStart, weekEnd);
  // Today's record is the timer's own query, refreshed by every clock action.
  const { data: todayRecord } = useGetTodayAttendance();
  const { data: sheets } = useGetMyTimeSheets(
    {
      pageNo: 1,
      pageSize: 50,
      filter: { UserId: userId, TaskDate: taskDateRangeClause(weekStart, weekEnd) },
      order: DEFAULT_MY_TIMESHEETS_ORDER,
    },
    canTimesheet
  );

  const isTodayOpen = isOpenRecord(todayRecord);
  // An open day's bar grows: re-read the clock every minute.
  useEffect(() => {
    if (!isTodayOpen) return undefined;
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, [isTodayOpen]);

  const attendanceOn = new Map((attendanceDays ?? []).map((row) => [row.day, row]));
  const records = (sheets as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets?.items ?? [];
  const loggedOn = new Map(
    records.map((record) => [record.TaskDate.slice(0, 10), loggedHours(record.Tasks)])
  );
  const weekday = (day: string) =>
    new Date(`${day}T00:00:00Z`).toLocaleDateString(i18n.language, {
      timeZone: 'UTC',
      weekday: 'short',
    });

  const bars = days.map((day) => {
    const row = attendanceOn.get(day);
    const record = day === today && todayRecord?.StartTimeUtc ? todayRecord : row?.record;
    const worked = workedSecondsOf(record, now) / 3600;
    const target = scheduledHours(day);
    // A day counts as under once it is over: an earlier day, or today after the last clock-out.
    const isFinished = day < today || (day === today && Boolean(record) && !isOpenRecord(record));
    const isUnder =
      target !== null && isFinished && Boolean(record?.StartTimeUtc) && worked < target;
    return { day, row, worked, target, isUnder, hasRecord: Boolean(record?.StartTimeUtc) };
  });
  const weekWorked = bars.reduce((sum, bar) => sum + bar.worked, 0);
  const underDays = bars.filter((bar) => bar.isUnder).length;

  return (
    <HomeCard title={t('HOME_THIS_WEEK')} viewTo={APP_PATHS.ATTENDANCE}>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-5 gap-2" role="list" aria-label={t('HOME_THIS_WEEK')}>
            {bars.map(({ day, row, worked, target, isUnder, hasRecord }) => {
              const logged = loggedOn.get(day);
              const status = row?.status;
              const label = `${weekday(day)}: ${hasRecord ? formatHours(worked) : '—'}`;
              return (
                <Tooltip key={day}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      role="listitem"
                      aria-label={label}
                      className="flex min-w-0 flex-col items-center gap-1 rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    >
                      <span
                        className={cn(
                          'text-xs tabular-nums',
                          isUnder ? 'font-medium text-destructive' : 'text-muted-foreground'
                        )}
                      >
                        {hasRecord ? formatHours(worked) : '—'}
                      </span>
                      <span
                        className="relative block w-full rounded-sm bg-muted"
                        style={{ height: COLUMN_HEIGHT }}
                      >
                        <span
                          className={cn(
                            'absolute inset-x-0 bottom-0 block rounded-sm',
                            isUnder ? 'bg-destructive' : 'bg-primary'
                          )}
                          style={{ height: percent(worked) }}
                        />
                        {target !== null && (
                          <span
                            className="absolute inset-x-0 block border-t border-dashed border-muted-foreground"
                            style={{ bottom: percent(target) }}
                          />
                        )}
                      </span>
                      <span
                        className={cn(
                          'text-xs',
                          day === today ? 'font-semibold text-primary' : 'text-muted-foreground'
                        )}
                      >
                        {weekday(day)}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="grid gap-0.5 text-xs">
                    <b>{weekday(day)}</b>
                    {status && <span>{row?.label || t(DAY_STATUS_LABEL_KEY[status])}</span>}
                    <span>
                      {t('HOME_WORKED')}: {hasRecord ? formatHours(worked) : '—'}
                      {target !== null && ` / ${formatHours(target)}`}
                    </span>
                    {canTimesheet && (
                      <span>
                        {t('HOME_LOGGED')}: {logged !== undefined ? formatHours(logged) : '—'}
                      </span>
                    )}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('HOME_WEEK_WORKED', { hours: formatHours(weekWorked) })}
            {underDays > 0 && (
              <span className="text-destructive">
                {' · '}
                {t('HOME_WEEK_UNDER_DAYS', { count: underDays })}
              </span>
            )}
          </p>
        </div>
      )}
    </HomeCard>
  );
};
